/**
 * Enhanced RBAC (Role-Based Access Control) Types
 * Extends the basic permission system for enterprise-grade multi-organization support
 */

import { User, Permission, Organization } from '../../../contracts/types';

// ============================================================================
// ENHANCED USER ROLES
// ============================================================================

export type EnhancedUserRole =
  | 'super_admin'     // Platform-wide admin across all organizations
  | 'org_admin'       // Organization administrator
  | 'org_manager'     // Organization manager with limited admin rights
  | 'team_lead'       // Team leader with user management rights
  | 'analyst'         // Data analyst with metrics and reporting access
  | 'viewer'          // Read-only access
  | 'external_viewer' // Limited read-only access for external users
  | 'api_user';       // Service account for API access

export type EnhancedPermission =
  // User Management
  | 'users:create'
  | 'users:read'
  | 'users:update'
  | 'users:delete'
  | 'users:invite'
  | 'users:suspend'
  | 'users:activate'
  | 'users:change_role'

  // Organization Management
  | 'organization:read'
  | 'organization:update'
  | 'organization:delete'
  | 'organization:invite_users'
  | 'organization:manage_settings'
  | 'organization:manage_billing'
  | 'organization:manage_integrations'

  // Agent Group Management
  | 'agent_groups:create'
  | 'agent_groups:read'
  | 'agent_groups:update'
  | 'agent_groups:delete'
  | 'agent_groups:configure'

  // Metrics Access
  | 'metrics:read'
  | 'metrics:export'
  | 'metrics:create_snapshots'
  | 'metrics:manage_alerts'

  // Experiments
  | 'experiments:create'
  | 'experiments:read'
  | 'experiments:update'
  | 'experiments:delete'
  | 'experiments:start'
  | 'experiments:stop'
  | 'experiments:approve'
  | 'experiments:clone'

  // Deployments
  | 'deployments:create'
  | 'deployments:read'
  | 'deployments:approve'
  | 'deployments:start'
  | 'deployments:rollback'
  | 'deployments:pause'
  | 'deployments:configure'

  // Reports
  | 'reports:read'
  | 'reports:create'
  | 'reports:share'
  | 'reports:export'
  | 'reports:schedule'

  // LakeFS Integration
  | 'lakefs:read_repositories'
  | 'lakefs:create_repositories'
  | 'lakefs:delete_repositories'
  | 'lakefs:create_branches'
  | 'lakefs:merge_branches'
  | 'lakefs:create_commits'
  | 'lakefs:create_tags'
  | 'lakefs:manage_policies'

  // System Administration
  | 'system:view_audit_logs'
  | 'system:manage_api_keys'
  | 'system:manage_webhooks'
  | 'system:manage_integrations'
  | 'system:view_system_metrics'

  // Security Management
  | 'security:read'
  | 'security:manage'
  | 'security:view_violations'
  | 'security:manage_rules'
  | 'security:block_users'
  | 'security:configure_csp'
  | 'security:configure_rate_limits';

// ============================================================================
// MULTI-ORGANIZATION SUPPORT
// ============================================================================

export interface UserOrganizationMembership {
  membership_id: string;
  user_id: string;
  org_id: string;
  role: EnhancedUserRole;
  permissions: EnhancedPermission[];
  status: 'active' | 'suspended' | 'pending_activation';
  created_at: string;
  updated_at: string;
  invited_by?: string;
  activated_at?: string;
  expires_at?: string; // For temporary access
}

export interface EnhancedUser extends Omit<User, 'role' | 'permissions' | 'organization'> {
  global_role: EnhancedUserRole;
  organization_memberships: UserOrganizationMembership[];
  active_org_id?: string; // Currently selected organization
  profile: UserProfile;
  security: UserSecurity;
  preferences: UserPreferences;
}

export interface UserProfile {
  first_name: string;
  last_name: string;
  display_name?: string;
  avatar_url?: string;
  bio?: string;
  timezone: string;
  language: string;
  phone?: string;
  department?: string;
  title?: string;
}

export interface UserSecurity {
  mfa_enabled: boolean;
  mfa_methods: ('totp' | 'sms' | 'email')[];
  password_last_changed: string;
  last_login: string;
  login_count: number;
  failed_login_attempts: number;
  account_locked: boolean;
  locked_until?: string;
  password_reset_required: boolean;
  session_timeout_minutes: number;
}

export interface UserPreferences {
  theme: 'light' | 'dark' | 'system';
  notifications: NotificationPreferences;
  dashboard: DashboardPreferences;
  privacy: PrivacyPreferences;
}

export interface NotificationPreferences {
  email_enabled: boolean;
  push_enabled: boolean;
  slack_enabled: boolean;
  notification_types: {
    experiment_completed: boolean;
    deployment_failed: boolean;
    cost_threshold_exceeded: boolean;
    accuracy_alert: boolean;
    system_maintenance: boolean;
    security_alert: boolean;
  };
}

export interface DashboardPreferences {
  default_time_range: string;
  default_metrics: string[];
  auto_refresh_enabled: boolean;
  auto_refresh_interval: number;
  charts_per_page: number;
  favorite_agent_groups: string[];
}

export interface PrivacyPreferences {
  activity_tracking: boolean;
  analytics_tracking: boolean;
  data_export_allowed: boolean;
}

// ============================================================================
// ROLE DEFINITIONS AND PERMISSIONS
// ============================================================================

export const ROLE_PERMISSIONS: Record<EnhancedUserRole, EnhancedPermission[]> = {
  super_admin: [
    // All permissions - platform-wide access
    'users:create', 'users:read', 'users:update', 'users:delete', 'users:invite',
    'users:suspend', 'users:activate', 'users:change_role',
    'organization:read', 'organization:update', 'organization:delete',
    'organization:invite_users', 'organization:manage_settings',
    'organization:manage_billing', 'organization:manage_integrations',
    'agent_groups:create', 'agent_groups:read', 'agent_groups:update',
    'agent_groups:delete', 'agent_groups:configure',
    'metrics:read', 'metrics:export', 'metrics:create_snapshots', 'metrics:manage_alerts',
    'experiments:create', 'experiments:read', 'experiments:update', 'experiments:delete',
    'experiments:start', 'experiments:stop', 'experiments:approve', 'experiments:clone',
    'deployments:create', 'deployments:read', 'deployments:approve',
    'deployments:start', 'deployments:rollback', 'deployments:pause', 'deployments:configure',
    'reports:read', 'reports:create', 'reports:share', 'reports:export', 'reports:schedule',
    'lakefs:read_repositories', 'lakefs:create_repositories', 'lakefs:delete_repositories',
    'lakefs:create_branches', 'lakefs:merge_branches', 'lakefs:create_commits',
    'lakefs:create_tags', 'lakefs:manage_policies',
    'system:view_audit_logs', 'system:manage_api_keys', 'system:manage_webhooks',
    'system:manage_integrations', 'system:view_system_metrics',
    'security:read', 'security:manage', 'security:view_violations',
    'security:manage_rules', 'security:block_users', 'security:configure_csp',
    'security:configure_rate_limits'
  ],

  org_admin: [
    // Organization-wide admin access
    'users:create', 'users:read', 'users:update', 'users:suspend', 'users:activate',
    'users:invite', 'users:change_role',
    'organization:read', 'organization:update', 'organization:invite_users',
    'organization:manage_settings', 'organization:manage_billing', 'organization:manage_integrations',
    'agent_groups:create', 'agent_groups:read', 'agent_groups:update',
    'agent_groups:delete', 'agent_groups:configure',
    'metrics:read', 'metrics:export', 'metrics:create_snapshots', 'metrics:manage_alerts',
    'experiments:create', 'experiments:read', 'experiments:update', 'experiments:delete',
    'experiments:start', 'experiments:stop', 'experiments:approve', 'experiments:clone',
    'deployments:create', 'deployments:read', 'deployments:approve',
    'deployments:start', 'deployments:rollback', 'deployments:pause', 'deployments:configure',
    'reports:read', 'reports:create', 'reports:share', 'reports:export', 'reports:schedule',
    'lakefs:read_repositories', 'lakefs:create_repositories', 'lakefs:create_branches',
    'lakefs:merge_branches', 'lakefs:create_commits', 'lakefs:create_tags', 'lakefs:manage_policies',
    'system:view_audit_logs', 'system:manage_api_keys', 'system:manage_webhooks',
    'security:read', 'security:manage', 'security:view_violations', 'security:block_users'
  ],

  org_manager: [
    // Organization management without user admin
    'users:read', 'users:invite',
    'organization:read', 'organization:update', 'organization:invite_users',
    'agent_groups:create', 'agent_groups:read', 'agent_groups:update', 'agent_groups:configure',
    'metrics:read', 'metrics:export', 'metrics:create_snapshots', 'metrics:manage_alerts',
    'experiments:create', 'experiments:read', 'experiments:update', 'experiments:start',
    'experiments:stop', 'experiments:approve', 'experiments:clone',
    'deployments:create', 'deployments:read', 'deployments:approve', 'deployments:start',
    'deployments:pause', 'deployments:configure',
    'reports:read', 'reports:create', 'reports:share', 'reports:export', 'reports:schedule',
    'lakefs:read_repositories', 'lakefs:create_branches', 'lakefs:merge_branches',
    'lakefs:create_commits', 'lakefs:create_tags'
  ],

  team_lead: [
    // Team leadership with limited user management
    'users:read', 'users:invite',
    'organization:read',
    'agent_groups:read', 'agent_groups:update', 'agent_groups:configure',
    'metrics:read', 'metrics:export', 'metrics:create_snapshots',
    'experiments:create', 'experiments:read', 'experiments:update', 'experiments:start',
    'experiments:stop', 'experiments:clone',
    'deployments:create', 'deployments:read', 'deployments:start', 'deployments:pause',
    'reports:read', 'reports:create', 'reports:share', 'reports:export',
    'lakefs:read_repositories', 'lakefs:create_branches', 'lakefs:create_commits'
  ],

  analyst: [
    // Data analysis and reporting focus
    'organization:read',
    'agent_groups:read',
    'metrics:read', 'metrics:export', 'metrics:create_snapshots',
    'experiments:read', 'experiments:create', 'experiments:clone',
    'deployments:read',
    'reports:read', 'reports:create', 'reports:share', 'reports:export',
    'lakefs:read_repositories', 'lakefs:create_branches', 'lakefs:create_commits'
  ],

  viewer: [
    // Read-only access
    'organization:read',
    'agent_groups:read',
    'metrics:read',
    'experiments:read',
    'deployments:read',
    'reports:read',
    'lakefs:read_repositories'
  ],

  external_viewer: [
    // Limited external access
    'metrics:read',
    'reports:read'
  ],

  api_user: [
    // Programmatic access for services
    'metrics:read', 'metrics:export',
    'experiments:read', 'experiments:create', 'experiments:start',
    'deployments:read', 'deployments:create',
    'lakefs:read_repositories', 'lakefs:create_commits'
  ]
};

// ============================================================================
// PERMISSION HELPERS
// ============================================================================

export interface PermissionContext {
  user: EnhancedUser;
  org_id: string;
  resource?: string;
  resource_owner_id?: string;
}

export class RBACService {
  /**
   * Check if user has a specific permission in the given organization
   */
  static hasPermission(
    context: PermissionContext,
    permission: EnhancedPermission
  ): boolean {
    const { user, org_id } = context;

    // Super admin has all permissions across all organizations
    if (user.global_role === 'super_admin') {
      return true;
    }

    // Find the user's membership in the specified organization
    const membership = user.organization_memberships.find(
      m => m.org_id === org_id && m.status === 'active'
    );

    if (!membership) {
      return false;
    }

    // Check if user has the specific permission
    return membership.permissions.includes(permission);
  }

  /**
   * Check if user has any of the specified permissions
   */
  static hasAnyPermission(
    context: PermissionContext,
    permissions: EnhancedPermission[]
  ): boolean {
    return permissions.some(permission =>
      this.hasPermission(context, permission)
    );
  }

  /**
   * Check if user has all of the specified permissions
   */
  static hasAllPermissions(
    context: PermissionContext,
    permissions: EnhancedPermission[]
  ): boolean {
    return permissions.every(permission =>
      this.hasPermission(context, permission)
    );
  }

  /**
   * Get all permissions for a user in a specific organization
   */
  static getUserPermissions(user: EnhancedUser, org_id: string): EnhancedPermission[] {
    const membership = user.organization_memberships.find(
      m => m.org_id === org_id && m.status === 'active'
    );

    if (!membership) {
      return [];
    }

    return membership.permissions;
  }

  /**
   * Get all organizations a user has access to
   */
  static getUserOrganizations(user: EnhancedUser): UserOrganizationMembership[] {
    return user.organization_memberships.filter(m => m.status === 'active');
  }

  /**
   * Check if user can access a specific resource
   */
  static canAccessResource(
    context: PermissionContext,
    resourceType: 'experiment' | 'deployment' | 'agent_group' | 'report'
  ): boolean {
    const readPermission = `${resourceType}s:read` as EnhancedPermission;
    return this.hasPermission(context, readPermission);
  }
}

// ============================================================================
// AUDIT LOGGING
// ============================================================================

export interface AuditLogEntry {
  id: string;
  user_id: string;
  user_email: string;
  org_id: string;
  action: string;
  resource_type: string;
  resource_id?: string;
  details: Record<string, any>;
  ip_address: string;
  user_agent: string;
  timestamp: string;
  success: boolean;
  error_message?: string;
}

export interface AuditLogQuery {
  user_id?: string;
  org_id?: string;
  action?: string;
  resource_type?: string;
  resource_id?: string;
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
}

// ============================================================================
// USER INVITATION SYSTEM
// ============================================================================

export interface UserInvitation {
  invitation_id: string;
  email: string;
  org_id: string;
  role: EnhancedUserRole;
  permissions: EnhancedPermission[];
  invited_by: string;
  created_at: string;
  expires_at: string;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  accepted_at?: string;
  revoked_at?: string;
  revoked_by?: string;
  welcome_message?: string;
}

export interface CreateInvitationRequest {
  email: string;
  org_id: string;
  role: EnhancedUserRole;
  custom_permissions?: EnhancedPermission[];
  welcome_message?: string;
  expires_in_hours?: number;
}

export interface AcceptInvitationRequest {
  invitation_id: string;
  password: string;
  first_name: string;
  last_name: string;
  timezone?: string;
}

export default RBACService;